import React from "react";
import _ from "lodash";
import PropTypes from "prop-types";

const Pagination = (props) => {
  const {
    itemsCount,
    pageSize,
    currentPage,
    onPageChange,
    startIndex,
    endIndex,
  } = props;

  const pagesCount = Math.ceil(itemsCount / pageSize);
  if (pagesCount <= 1) return null;
  const pages = _.range(1, pagesCount + 1);
  const visiblePages = pages.slice(startIndex, endIndex);

  const showLeadingEllipsis = startIndex > 0;
  const showTrailingEllipsis = endIndex < pagesCount;

  const itemStart = (currentPage - 1) * pageSize + 1;
  const itemEnd = Math.min(currentPage * pageSize, itemsCount);

  return (
    <>
      <style>{`
        @keyframes pgFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .pg-wrapper {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          padding: 14px 20px;
          background: #ffffff;
          border-top: 1px solid #f1f5f9;
          border-radius: 0;
          animation: pgFadeIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        .pg-info {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 14px;
          font-weight: 500;
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          color: #1e293b;
          background: transparent;
          border: 1px solid rgba(100, 116, 139, 0.18);
          border-radius: 0;
          padding: 5px 12px;
          letter-spacing: 0.01em;
          white-space: nowrap;
        }

        .pg-info strong {
          color: #1e293b;
          font-weight: 500;
        }

        .pg-info i {
          color: #1e293b;
          font-size: 14px;
        }

        .pg-nav {
          display: flex;
          align-items: center;
          gap: 4px;
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .pg-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 34px;
          height: auto;
          min-height: unset;
          padding: 7px 14px;
          border-radius: 0;
          border: 1px solid #cbd5e1;
          background: #ffffff;
          color: #1e293b;
          font-size: 14px;
          font-weight: 500;
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          letter-spacing: 0.01em;
          line-height: 1.25;
          cursor: pointer;
          transition: all 0.2s ease;
          outline: none;
          user-select: none;
          box-shadow: 0 2px 5px rgba(15, 23, 42, 0.08);
          text-decoration: none !important;
        }

        .pg-btn:hover:not(.pg-btn--disabled):not(.pg-btn--active) {
          background: #f8fafc;
          border-color: #94a3b8;
          color: #0f172a;
          transform: none;
          box-shadow: 0 3px 8px rgba(15, 23, 42, 0.12);
        }

        .pg-btn:active:not(.pg-btn--disabled):not(.pg-btn--active) {
          transform: none;
          box-shadow: 0 2px 5px rgba(15, 23, 42, 0.08);
        }

        .pg-btn--active {
          background: #171717;
          border-color: #171717;
          color: #ffffff;
          box-shadow: 0 2px 5px rgba(15, 23, 42, 0.2);
          cursor: default;
          font-weight: 500;
        }

        .pg-btn--disabled {
          opacity: 0.38;
          cursor: not-allowed;
          pointer-events: none;
          box-shadow: none;
        }

        .pg-btn--nav {
          border-radius: 0;
          background: #ffffff;
          color: #1e293b;
        }

        .pg-btn--nav:hover:not(.pg-btn--disabled) {
          background: #f8fafc;
          border-color: #94a3b8;
          color: #0f172a;
          transform: none;
          box-shadow: 0 3px 8px rgba(15, 23, 42, 0.12);
        }

        .pg-ellipsis {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 34px;
          height: 34px;
          color: #64748b;
          font-size: 14px;
          font-weight: 500;
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          letter-spacing: 1px;
          user-select: none;
        }
      `}</style>

      <div className="pg-wrapper">
        {/* Info strip */}
        <span className="pg-info">
          <i className="fa fa-list-ol" aria-hidden="true" />
          Showing <strong>{itemStart}–{itemEnd}</strong> of <strong>{itemsCount}</strong> results
          &nbsp;·&nbsp; Page <strong>{currentPage}</strong> of <strong>{pagesCount}</strong>
        </span>

        {/* Page buttons */}
        <nav aria-label="Pagination">
          <ul className="pg-nav">
            {/* First page */}
            <li>
              <button
                className={`pg-btn pg-btn--nav${currentPage === 1 ? " pg-btn--disabled" : ""}`}
                onClick={() => onPageChange(1)}
                disabled={currentPage === 1}
                title="First page"
                aria-label="First page"
              >
                <i className="fa fa-angle-double-left" aria-hidden="true" />
              </button>
            </li>

            {/* Previous */}
            <li>
              <button
                className={`pg-btn pg-btn--nav${currentPage === 1 ? " pg-btn--disabled" : ""}`}
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                title="Previous page"
                aria-label="Previous page"
              >
                <i className="fa fa-angle-left" aria-hidden="true" />
              </button>
            </li>

            {/* Leading ellipsis */}
            {showLeadingEllipsis && (
              <li>
                <span className="pg-ellipsis" aria-hidden="true">···</span>
              </li>
            )}

            {/* Numbered pages */}
            {visiblePages.map((page) => (
              <li key={page}>
                <button
                  className={`pg-btn${page === currentPage ? " pg-btn--active" : ""}`}
                  onClick={() => page !== currentPage && onPageChange(page)}
                  aria-current={page === currentPage ? "page" : undefined}
                  aria-label={`Page ${page}`}
                >
                  {page}
                </button>
              </li>
            ))}

            {/* Trailing ellipsis */}
            {showTrailingEllipsis && (
              <li>
                <span className="pg-ellipsis" aria-hidden="true">···</span>
              </li>
            )}

            {/* Next */}
            <li>
              <button
                className={`pg-btn pg-btn--nav${currentPage === pagesCount ? " pg-btn--disabled" : ""}`}
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === pagesCount}
                title="Next page"
                aria-label="Next page"
              >
                <i className="fa fa-angle-right" aria-hidden="true" />
              </button>
            </li>

            {/* Last page */}
            <li>
              <button
                className={`pg-btn pg-btn--nav${currentPage === pagesCount ? " pg-btn--disabled" : ""}`}
                onClick={() => onPageChange(pagesCount)}
                disabled={currentPage === pagesCount}
                title="Last page"
                aria-label="Last page"
              >
                <i className="fa fa-angle-double-right" aria-hidden="true" />
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </>
  );
};

Pagination.propTypes = {
  itemsCount: PropTypes.number.isRequired,
  pageSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  currentPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  startIndex: PropTypes.number,
  endIndex: PropTypes.number,
};

Pagination.defaultProps = {
  startIndex: 0,
  endIndex: 5,
};

export default Pagination;
